'use client'

import React, { useState } from 'react'
import Image from 'next/image'
import { CarouselContent, CarouselItem } from '@/components/ui/carousel'
import type { CarouselApi } from '@/components/ui/carousel'
import { CarouselBlockShell } from '@/components/ui/carousel-block-shell'
import { useCarouselCurrentIndex } from '@/components/ui/use-carousel-current-index'
import type { Campaign, Icon } from '@/payload-types'
import { isCampaignActive } from '@/lib/campaign-active'
import { getCampaignImage, getCampaignLinkHref } from '@/lib/campaign-carousel-helpers'
import { cn } from '@/lib/utils'
import { IconComponent } from '@/components/ui/icon'

export function CampaignCarouselClient({
  campaigns,
  inactiveLabel,
  showYear = false,
  icon,
  iconLeft,
  iconRight,
}: {
  campaigns: Campaign[]
  inactiveLabel?: string
  showYear?: boolean
  icon?: (string | null) | Icon
  iconLeft?: (string | null) | Icon
  iconRight?: (string | null) | Icon
}) {
  const [api, setApi] = useState<CarouselApi>()
  const count = campaigns.length
  const current = useCarouselCurrentIndex(api, count)

  if (!count) return null

  const effectiveInactiveLabel = inactiveLabel?.trim() || undefined

  const renderedCampaigns = count === 3 ? [...campaigns, ...campaigns] : campaigns
  const slideBasisClass = cn(
    'basis-[85%] sm:basis-[75%]',
    count <= 2 ? 'md:basis-[70%] lg:basis-[65%]' : count === 3 ? 'basis-3/4 md:basis-1/2 lg:basis-[35%]' : 'md:basis-1/2 lg:basis-1/3',
  )

  return (
    <CarouselBlockShell
      ariaLabel="Featured campaigns"
      setApi={setApi}
      opts={{ align: 'center', containScroll: false, loop: count > 1 }}
      iconLeft={iconLeft}
      iconRight={iconRight}
      count={count}
      current={current}
      onSelect={(index) => api?.scrollTo(index)}
    >
      <CarouselContent className="ml-0">
        {renderedCampaigns.map((campaign, i) => {
          const image = getCampaignImage(campaign)
          const active = isCampaignActive(campaign)
          const href = active ? getCampaignLinkHref(campaign) : null
          const normalizedIndex = i % count
          const showInactiveLabel = Boolean(effectiveInactiveLabel) && !active
          const showInactiveYear = showYear && !active
          const endDate = new Date(campaign.endDate)
          const endYear = Number.isNaN(endDate.getTime()) ? null : endDate.getUTCFullYear()

          const card = (
            <article className={cn("theme-card mt-4 mb-6 overflow-hidden border transition-all mx-auto w-full max-w-[80vw] md:max-w-125", normalizedIndex !== current && "scale-95 origin-center")}>
              <div className="relative aspect-square w-full">
                {icon && <IconComponent icon={icon} className={cn("absolute w-12 h-12 -top-4 -right-4 z-10 transition-all duration-700 animate-twinkle", normalizedIndex === current ? "scale-100" : "scale-0")} />}
                {image ? (
                  <Image
                    src={image.url}
                    alt={campaign.name}
                    fill
                    sizes="(max-width: 640px) 85vw, (max-width: 768px) 75vw, (max-width: 1024px) 50vw, 33vw"
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <p>No image</p>
                )}
              </div>
              <div className="flex flex-1 flex-col items-center justify-center gap-1 px-theme-button-px py-theme-button-py text-center bg-theme-button-primary-background border-t border-theme-surface-border">
                <h3>{campaign.name}</h3>
                {showInactiveLabel ? (
                  <p>{effectiveInactiveLabel}</p>
                ) : null}
                {showInactiveYear && endYear ? (
                  <p>{endYear}</p>
                ) : null}
              </div>
            </article>
          )

          return (
            <CarouselItem key={`${campaign.id}-${i}`} className={cn(slideBasisClass, 'pl-0')}>
              {href ? (
                <a href={href} className="no-underline">
                  {card}
                </a>
              ) : (
                card
              )}
            </CarouselItem>
          )
        })}
      </CarouselContent>
    </CarouselBlockShell>
  )
}
